import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyEmail } from '../lib/api';
import { setToken, setUser } from '../lib/auth';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, ArrowRight, RefreshCw } from 'lucide-react';

export default function VerifyEmailPage() {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const navigate = useNavigate();
  
  // Extract token from URL - handles various mobile email client formats
  const extractToken = () => {
    // Get the full URL
    const fullUrl = window.location.href;
    const searchString = window.location.search;
    
    console.log('Full URL:', fullUrl);
    console.log('Search string:', searchString);
    
    // Method 1: Standard URLSearchParams
    const params = new URLSearchParams(searchString);
    let token = params.get('token');
    
    if (token) {
      console.log('Token from URLSearchParams:', token);
      return token.trim();
    }
    
    // Method 2: Manual parsing (for edge cases)
    const tokenMatch = fullUrl.match(/[?&]token=([^&\s#]+)/);
    if (tokenMatch) {
      token = tokenMatch[1];
      console.log('Token from regex:', token);
      // Decode if URL encoded
      try {
        token = decodeURIComponent(token);
      } catch (e) {
        // Not encoded
      }
      return token.trim();
    }
    
    // Method 3: Check hash (some email clients put params after #)
    const hash = window.location.hash;
    if (hash && hash.includes('token=')) {
      const hashMatch = hash.match(/token=([^&\s]+)/);
      if (hashMatch) {
        token = hashMatch[1];
        try {
          token = decodeURIComponent(token);
        } catch (e) {}
        console.log('Token from hash:', token);
        return token.trim();
      }
    }
    
    return null;
  };

  const doVerification = async (token) => {
    if (!token) {
      setStatus('error');
      setError('Invalid verification link. No token found in the URL.');
      return;
    }

    try {
      console.log('Sending verification request with token:', token.substring(0, 10) + '...');
      const response = await verifyEmail(token);
      
      // Store auth data
      setToken(response.data.token);
      setUser(response.data.user);
      
      setStatus('success');
      toast.success('Email verified successfully!');
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Verification error:', err.response?.data || err);
      setStatus('error');
      const errorMsg = err.response?.data?.detail || 'Verification failed. The link may have expired or already been used.';
      setError(errorMsg);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    setStatus('verifying');
    const token = extractToken();
    await doVerification(token);
    setRetrying(false);
  };

  useEffect(() => {
    const token = extractToken();
    doVerification(token);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      {/* Floating Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-slide-in">
          <div className="w-24 h-24 mx-auto mb-3 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/logo-new.png" 
              alt="GuestCheckout" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Status Card */}
        <div className="card-3d p-8 text-center animate-slide-in" style={{ animationDelay: '0.1s' }}>
          {status === 'verifying' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center">
                <Loader2 size={36} className="text-white animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">Verifying Email...</h2>
              <p className="text-slate-600">Please wait while we verify your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <CheckCircle size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">Email Verified!</h2>
              <p className="text-slate-600 mb-6">Your account has been activated successfully.</p>
              <p className="text-sm text-slate-500 mb-6">Redirecting to dashboard...</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="btn-3d btn-3d-primary w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                Go to Dashboard
                <ArrowRight size={18} />
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
                <XCircle size={40} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-3">Verification Failed</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  disabled={retrying}
                  className="btn-3d btn-3d-outline w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  {retrying ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <RefreshCw size={18} />
                  )}
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="btn-3d btn-3d-primary w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  Register Again
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Back to Login
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} GuestCheckoutapp. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
